// The rooms' lights switched on (docs/claude/lights.md, *Lights on*): lights.ts's
// emitters as two rigs per room — the view's, for Lights on, and the file's, for
// a GLB (glTF holds spot and point lights, no area light) —, each hidden until the
// viewer shows it.

import * as THREE from 'three';
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js';
import type { Vec2 } from '../../shared/types';
import {
  type Emitter,
  rigLines,
  type Vec3,
} from '../lights';

/** Every light shines 2700 K white — an RGB one too, its colour chosen on site. */
export const LIGHT_2700K = '#ffa757';

/** A fixture's glow: in the normal view, and with the lights on. */
export const GLOW = { off: 1.1, on: 4 };

/** A spot's light (the scene is in mm, the falloff physical) and its soft edge. */
const SPOT = { intensity: 1.3e7, penumbra: 0.65 };
/** A linear light: its area light's width across (mm) and intensity, by what it is. */
const LINE = { width: 40, intensity: { strip: 14, cove: 18, channel: 14 } };
/** In a file, a linear light is point lights this far apart (mm), each its share. */
const FILE_SPACING = 500;
/** How far off its fixture a light stands, along its aim (mm). */
const OFF = 8;

let areaLightsReady = false;

/** A room's two rigs: `W` takes a plan point from its frame to the world's, `rot`
 *  is its rotation (degrees CCW); a spot opens as its beam (`Emitter.beam`). The
 *  build records which is which (sceneNodes.ts, `viewRig` / `fileRig`). */
export function lightRigs(emitters: readonly Emitter[], W: (p: Vec2) => Vec2, rot: number): { view: THREE.Group; file: THREE.Group } {
  if (!areaLightsReady) { RectAreaLightUniformsLib.init(); areaLightsReady = true; }
  const view = new THREE.Group(), file = new THREE.Group();
  view.visible = false; file.visible = false;
  const r = (rot * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  const at = (p: Vec3): THREE.Vector3 => { const w = W({ x: p[0], y: p[1] }); return new THREE.Vector3(w.x, p[2], -w.y); };
  const dir = (v: Vec3): THREE.Vector3 => new THREE.Vector3(c * v[0] - s * v[1], v[2], -(s * v[0] + c * v[1])).normalize();
  // A spot keeps its target as a child one unit down its axis, so the glTF
  // exporter writes its direction.
  const spot = (pos: THREE.Vector3, aim: THREE.Vector3, beam: number): THREE.SpotLight => {
    const L = new THREE.SpotLight(LIGHT_2700K, SPOT.intensity, 0, (beam / 2) * (Math.PI / 180), SPOT.penumbra, 2);
    L.position.copy(pos);
    L.lookAt(pos.clone().add(aim));
    L.add(L.target);
    L.target.position.set(0, 0, -1);
    return L;
  };
  for (const e of emitters) {
    if (e.kind !== 'spot') continue;
    const aim = dir(e.aim), pos = at(e.at).addScaledVector(aim, OFF);
    view.add(spot(pos, aim, e.beam.angle));
    file.add(spot(pos, aim, e.beam.angle));
  }
  // A line light's straight pieces (`rigLines`: a rounded corner one or two, not
  // ten), each as bright as the stretches it stands for.
  for (const l of rigLines(emitters)) {
    const aim = dir(l.aim), intensity = LINE.intensity[l.role] * l.power;
    const A = at(l.a).addScaledVector(aim, OFF), B = at(l.b).addScaledVector(aim, OFF), len = A.distanceTo(B);
    const area = new THREE.RectAreaLight(LIGHT_2700K, intensity, LINE.width, len);
    area.position.copy(A).add(B).multiplyScalar(0.5);
    area.up.copy(B).sub(A).normalize();
    area.lookAt(area.position.clone().add(aim));
    view.add(area);
    // Its power (intensity × area × π) shared out as points (each power / 4π).
    const n = Math.max(1, Math.round(len / FILE_SPACING));
    const each = (intensity * LINE.width * len) / (4 * n);
    for (let k = 0; k < n; k++) {
      const P = new THREE.PointLight(LIGHT_2700K, each, 0, 2);
      P.position.copy(A).lerp(B, (k + 0.5) / n);
      file.add(P);
    }
  }
  return { view, file };
}
